import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { CONTENT_KEYS } from '@core/models/content-keys';
import { BaseActionHandlerService } from '@core/services/action-handler/base-action-handler.service';
import { ContentService } from '@core/services/content.service';
import { JexlService } from '@core/services/jexl.service';
import { ModalService } from '@core/services/modal.service';
import { UpdateService } from '@core/services/update.service';

import { UniversalModalTemplate } from '@shared/universal-modal-template/universal-modal-template';
import type { ComponentActionEvent } from '@shared/universal-page-template/universal-page-template.types';

import type { AppUpdateModalContent, CmsAction, CmsUpdateItem } from './app-update-modal.types';

@Component({
  selector: 'app-update-modal',
  template: `<app-universal-modal-template
    [state]="state()"
    [title]="title()"
    [showCloseButton]="showCloseButton()"
    [groups]="groups()"
    (closeEvent)="onClose()"
    (componentAction)="onAction($event)"
  /> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UniversalModalTemplate],
  providers: [ContentService],
})
export class AppUpdateModal {
  private readonly modalService = inject(ModalService);
  private readonly jexlService = inject(JexlService);
  private readonly contentService = inject(ContentService<AppUpdateModalContent>);
  private readonly baseActionHandler = inject(BaseActionHandlerService);
  private readonly updateService = inject(UpdateService);

  readonly data = input<Record<string, unknown>>({});

  protected readonly state = this.contentService.state;

  private readonly jexlData = computed(() => {
    const raw = this.data();

    // Transform actions into button format
    const actions = (raw['actions'] as CmsAction[] | undefined) ?? [];
    const buttons = actions.map((action) => ({
      label: action.label,
      severity: action.isPrimary ? 'primary' : 'secondary',
      outlined: !action.isPrimary,
      fluid: true,
      rounded: true,
      size: 'large',
      action: { id: action.action },
    }));

    // Transform updates into InfoListCard format
    const updates = (raw['updates'] as CmsUpdateItem[] | undefined) ?? [];
    const updatesListConfig =
      updates.length > 0
        ? {
            items: updates.map((update) => ({
              icon: 'checkmark-circle',
              title: update.title,
              descriptions: [update.body],
            })),
            showBackgroundGradient: true,
            iconGradient: 'primary' as const,
          }
        : undefined;

    return { ...raw, buttons, updatesListConfig };
  });

  protected readonly groups = computed(() => {
    const template = this.state().getData();
    if (!template?.groups) return [];
    return this.jexlService.processGroups(template.groups, this.jexlData());
  });

  protected readonly title = computed(() => {
    const template = this.state().getData();
    if (!template?.title) return undefined;
    return this.jexlService.process({ title: template.title }, this.jexlData()).title as string;
  });

  protected readonly showCloseButton = computed(() => !!this.data()['canDismiss']);

  constructor() {
    this.contentService.loadFeatureContent(CONTENT_KEYS.MODALS._id, CONTENT_KEYS.MODALS.APP_UPDATE);
  }

  protected onClose(): void {
    this.modalService.dismissLatest();
  }

  protected onAction(event: ComponentActionEvent): void {
    this.baseActionHandler.handleAction(event, {
      openAppStore: () => this.updateService.openAppStore(),
    });
  }
}
